import React, { useContext, useEffect, useMemo, useState } from 'react'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal'
import { DemoAccountDetails } from 'api/Clients/Types/DemoAccountDetails'
import { TradingAccountDetailDto } from 'api/Clients/Types/TradingAccountDetails'
import { WelcomeAccountDetails } from 'api/Clients/Types/WelcomeAccountDetails'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { ResetTradingAccountPassword } from 'api/Trading-Accounts/Types/ResetTradingAccountPassword'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchTradingAccountRestrictions,
  fetchTradingAccountStatusAction,
  getCampaignBusinessRuleTypesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'

import type { ClientAreaAuditLogProps } from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLog'
import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'

interface Props {
  data: TradingAccountDetailDto | DemoAccountDetails | WelcomeAccountDetails
  render: (data: TradingAccountDetailsProps) => React.ReactNode
  type: 'demoAccount' | 'welcomeAccount' | 'tradingAccount'
  resetTradingAccountPassword(values: ResetTradingAccountPassword): void
}

const getAuditLogProps = ({
  type,
  id,
}: {
  type: 'demoAccount' | 'welcomeAccount' | 'tradingAccount'
  id: string
}): ClientAreaAuditLogProps => {
  switch (type) {
    case 'demoAccount':
      return { demoAccountId: id }
    case 'tradingAccount':
      return { tradingAccountId: id }
    case 'welcomeAccount':
      return { welcomeAccountId: id }
    default:
      return { tradingAccountId: id }
  }
}

export interface TradingAccountDetailsProps {
  data: TradingAccountDetailDto | DemoAccountDetails | WelcomeAccountDetails
  isEdit: boolean
  tradingAccountStatus: NameDto[]
  tradingAccountRestrictions: RestrictionDto[]
  campaignBusinessRuleTypes: NameDto[]
  permissions: PermissionDto[]
  setEdit(value: boolean): void
  resetPassword(values: { withInvestorPassword?: boolean }): void
  setAuditLogModal(value: boolean): void
  setConfirmationModal(value: {
    visible: boolean
    action?: 'toggleExcludeFromArchiving' | 'archiveAccount'
    onSuccess?: () => void
  }): void
}

enum TMTHiddenRestrictions {
  LeverageDown = 21,
  LeverageUp = 22,
  ChangeInvestorPassword = 23,
  ResetInvestorPassword = 25,
}

const TradingAccountDetailsPage: React.FC<Props> = ({
  data,
  resetTradingAccountPassword,
  type,
  render,
}) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [isEdit, setEdit] = useState(false)
  const [auditLogModal, setAuditLogModal] = useState(false)

  const [confirmationModal, setConfirmationModal] = useState<{
    visible: boolean
    action?: 'toggleExcludeFromArchiving' | 'archiveAccount'
    onSuccess?: () => void
  }>({
    visible: false,
  })
  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const tradingAccountRestrictions = useAppSelector((state) => state.tradingAccounts.restrictions)
  const tradingAccountStatus = useAppSelector((state) => state.tradingAccounts.tradingAccountStatus)
  const campaignBusinessRuleTypes = useAppSelector(
    (state) => state.tradingAccounts.campaignBusinessRuleTypes
  )

  const filteredTARestrictions = useMemo(() => {
    if (data.accountGroup.platformType.id !== PlatformTypeId.TickmillTrader)
      return tradingAccountRestrictions
    return tradingAccountRestrictions.filter(
      (r) => !Object.values(TMTHiddenRestrictions).includes(r.id)
    )
  }, [tradingAccountRestrictions, data.accountGroup.platformType.id])

  useEffect(() => {
    ;(async () => {
      if (isEmpty(tradingAccountStatus)) {
        dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient))
      }
      if (isEmpty(tradingAccountRestrictions)) {
        dispatch(fetchTradingAccountRestrictions(tradingAccountsApiClient))
      }
      if (isEmpty(campaignBusinessRuleTypes)) {
        dispatch(getCampaignBusinessRuleTypesAction(tradingAccountsApiClient))
      }
    })()
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const resetPassword = async ({ withInvestorPassword }: { withInvestorPassword?: boolean }) => {
    if (withInvestorPassword) {
      await resetTradingAccountPassword({
        changeInvestorPassword: true,
        tradingAccountId: data.id,
      })
      setToast({
        type: 'success',
        title: 'Investor password has been reset',
        subtitle: 'Password has been sent to client email',
      })
    } else {
      await resetTradingAccountPassword({
        changeInvestorPassword: false,
        tradingAccountId: data.id,
      })
      setToast({
        type: 'success',
        title: 'Master password has been reset',
        subtitle: 'Password has been sent to client email',
      })
    }
  }
  const handleToggleExcludeFromArchiving = async () => {
    if (!('isExcludedFromArchiving' in data)) return

    try {
      await tradingAccountsApiClient.updateTradingAccountArchivingExclusion(data.id, {
        isExcludedFromArchiving: !data.isExcludedFromArchiving,
      })

      setToast({
        type: 'success',
        title: data.isExcludedFromArchiving
          ? 'Trading account included in archiving.'
          : 'Trading account excluded from archiving.',
      })

      confirmationModal.onSuccess?.()
    } catch {
      setToast({
        type: 'danger',
        title: 'Failed to update archiving exclusion.',
      })
    } finally {
      setConfirmationModal({ visible: false })
    }
  }
  const handleArchiveTradingAccount = async () => {
    const response = await tradingAccountsApiClient.archiveTradingAccountsByNames({
      tradingAccountNames: [data.name],
    })

    if (response.archived?.includes(data.name)) {
      setToast({
        type: 'success',
        title: 'Trading account archived.',
      })
    }

    if (response.failed?.includes(data.name)) {
      setToast({
        type: 'danger',
        title: 'Trading account archiving failed.',
      })
    }

    if (response.skipped?.includes(data.name)) {
      setToast({
        type: 'warning',
        title: 'Trading account archiving was skipped.',
      })
    }

    if (response.excludedFromArchiving?.includes(data.name)) {
      setToast({
        type: 'warning',
        title: 'Trading account is excluded from Archiving',
      })
    }
    confirmationModal.onSuccess?.()

    setConfirmationModal({
      visible: false,
    })
  }
  return (
    <React.Fragment>
      {auditLogModal && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal}
          onClose={() => setAuditLogModal(false)}
          {...getAuditLogProps({ type, id: data.id })}
        />
      )}
      {confirmationModal.visible && (
        <ConfirmationModal
          visible={confirmationModal.visible}
          yesButtonText='Proceed'
          renderTitle={
            <div>
              <p>Please confirm you want to do the following action:</p>

              <div className='mt-4'>
                <strong>
                  {confirmationModal.action === 'toggleExcludeFromArchiving'
                    ? 'Toggle Exclude From Archiving'
                    : 'Archive Account'}
                </strong>
              </div>
            </div>
          }
          onCancel={() =>
            setConfirmationModal({
              visible: false,
            })
          }
          onClose={() =>
            setConfirmationModal({
              visible: false,
            })
          }
          onYes={async () => {
            if (confirmationModal.action === 'toggleExcludeFromArchiving') {
              await handleToggleExcludeFromArchiving()
            }

            if (confirmationModal.action === 'archiveAccount') {
              await handleArchiveTradingAccount()
            }

            setConfirmationModal({
              visible: false,
            })
          }}
        />
      )}
      {render({
        data,
        isEdit,
        tradingAccountStatus,
        campaignBusinessRuleTypes,
        tradingAccountRestrictions: filteredTARestrictions,
        permissions,
        setEdit,
        resetPassword,
        setAuditLogModal,
        setConfirmationModal,
      })}
    </React.Fragment>
  )
}

export default TradingAccountDetailsPage
